import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
	ChevronDownIcon,
	ArrowsUpDownIcon,
	ArrowLongDownIcon,
	ArrowLongUpIcon,
} from '@heroicons/react/20/solid';

const ICON_MAP = {
	arrowDown: ArrowLongDownIcon,
	arrowUp: ArrowLongUpIcon,
};

/**
 * Popover de selección única (radio) con diseño consistente al PopoverCheckboxFilter.
 * Pensado para listas cortas de opciones (2–4), como "Ordenar por".
 *
 * @param {string|number} value            Valor seleccionado.
 * @param {function}      onChange         (nextValue) => void
 * @param {Array}         options          [{ value, label, description?, iconName? }]
 * @param {string}        title            Título del header.
 * @param {string}        subtitle         Subtítulo del header.
 * @param {Element}       icon             Componente de icono para el header/botón.
 * @param {string}        placeholderLabel Texto cuando no hay valor.
 */
export const PopoverRadioFilter = ({
	value,
	onChange,
	options = [],
	title = 'Ordenar por',
	subtitle,
	icon: Icon = ArrowsUpDownIcon,
	placeholderLabel = 'Seleccionar',
}) => {
	const selectedOption = options.find((opt) => opt.value === value);
	const summary = selectedOption?.label || placeholderLabel;
	const hasSelection = Boolean(selectedOption);

	return (
		<div className='flex w-full text-center justify-center'>
			<Menu as='div' className='w-full relative inline-block text-left'>
				<div>
					<Menu.Button
						className={`inline-flex w-full justify-center items-center rounded-md bg-white px-4 py-2 text-md font-medium shadow-sm ring-1 ring-inset ${hasSelection ? 'ring-gray-400 text-gray-900' : 'ring-gray-300 text-gray-700'} hover:bg-gray-50 hover:text-gray-900 hover:ring-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`}
					>
						<Icon className='mr-2 h-4 w-4 text-gray-500' aria-hidden='true' />
						{summary}
						<ChevronDownIcon className='-mr-1 ml-2 h-5 w-5 text-gray-500' aria-hidden='true' />
					</Menu.Button>
				</div>

				<Transition
					as={Fragment}
					enter='transition ease-out duration-150'
					enterFrom='transform opacity-0 scale-95 translate-y-1'
					enterTo='transform opacity-100 scale-100 translate-y-0'
					leave='transition ease-in duration-100'
					leaveFrom='transform opacity-100 scale-100 translate-y-0'
					leaveTo='transform opacity-0 scale-95 translate-y-1'
				>
					<Menu.Items className='absolute right-0 mt-2 w-full md:w-[320px] origin-top-right overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-200 focus:outline-none z-50'>
						<div className='flex items-start gap-3 bg-gradient-to-br from-indigo-50/70 via-white to-white px-5 py-4 border-b border-gray-100'>
							<div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600'>
								<Icon className='h-5 w-5' aria-hidden='true' />
							</div>
							<div className='text-left'>
								<h3 className='text-sm font-semibold text-gray-900'>{title}</h3>
								{
									subtitle && (
										<p className='mt-0.5 text-xs text-gray-500 leading-snug'>
											{subtitle}
										</p>
									)
								}
							</div>
						</div>

						<div className='flex flex-col gap-2 px-5 py-4'>
							{
								options.map((option) => {
									const active = value === option.value;
									const OptionIcon = option.iconName ? ICON_MAP[option.iconName] : null;
									return (
										<Menu.Item key={option.value}>
											{({ close }) => (
												<button
													type='button'
													onClick={() => {
														onChange?.(option.value);
														close();
													}}
													className={`group flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 focus:outline-none ${
														active
															? 'border-indigo-500 bg-indigo-50/60 ring-1 ring-indigo-200'
															: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
													}`}
												>
													<span
														className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-150 ${
															active
																? 'border-indigo-600'
																: 'border-gray-300 group-hover:border-gray-400'
														}`}
														aria-hidden='true'
													>
														<span
															className={`h-2.5 w-2.5 rounded-full transition-all duration-150 ${
																active ? 'scale-100 bg-indigo-600' : 'scale-0 bg-transparent'
															}`}
														/>
													</span>
													<div className='min-w-0 flex-1'>
														<div className='flex items-center gap-2'>
															{
																OptionIcon && (
																	<OptionIcon
																		className={`h-4 w-4 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`}
																		aria-hidden='true'
																	/>
																)
															}
															<span className={`text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-800'}`}>
																{option.label}
															</span>
														</div>
														{
															option.description && (
																<p className='mt-0.5 text-[11px] text-gray-500 leading-snug'>
																	{option.description}
																</p>
															)
														}
													</div>
												</button>
											)}
										</Menu.Item>
									);
								})
							}
						</div>
					</Menu.Items>
				</Transition>
			</Menu>
		</div>
	);
};
